import React, { useEffect, useMemo } from 'react';
import classNames from 'classnames';
import { useHistory, useParams } from 'react-router';
import useTranslation from '~/app/hooks/useTranslation';
import usePageTitle from '~/app/hooks/usePageTitle';
import { useAppHeader } from '~/app/modules/appHeader';
import { route } from '~/app/helpers/route';
import Icon from '~/app/common/Icon';
import Loader from '~/app/common/Loader';
import { IssueOrServiceType } from '~/app/data/statuses/chat';
import { useStoreActions, useStoreState } from '~/store';
import Attachment from '~/workorder/Detail/Attachment';
import { WORKORDER_ROUTES } from '~/workorder/Router';
import classes from './style.module.scss';
import Users from './Users';
import Parts from './Parts';
import Materials from './Material';

export type DetailComponent = React.FC;

const Detail: DetailComponent = () => {
    
    const { id, taskId, type } = useParams<{ id: string; taskId: string; type: string }>();

    const history = useHistory();
    const { setData } = useAppHeader();
    const { t } = useTranslation('workorder');
    const { t: tIssue } = useTranslation('issue');
    const { t: tService } = useTranslation('service');

    const fetching = useStoreState(state => state.workOrder.fetching);
    const taskData = useStoreState(state => state.workOrder.taskData);
    const getWorkOrderTaskDetail = useStoreActions(actions => actions.workOrder.getWorkOrderTaskDetail);

    const typeLabel = t(`detail.taskType.${taskData.type}`);
    const isIssue = taskData.type === IssueOrServiceType.ISSUE;

    const descriptionBody = useMemo(() => {
        const text = taskData.description?.trim();
        return text || '-';
    }, [taskData.description]);

    const users = useMemo(() => taskData.users ?? [], [taskData.users]);
    const parts = useMemo(() => taskData.parts ?? [], [taskData.parts]);
    const materials = useMemo(() => taskData.materials ?? [], [taskData.materials]);

    const pageTitle = taskData.title || t('detail.tasks');
    usePageTitle(pageTitle);

    useEffect(() => {
        if (taskId) {
            const typeParam = type === 'issue' ? IssueOrServiceType.ISSUE : IssueOrServiceType.SERVICE;
            getWorkOrderTaskDetail({ id: Number(id), taskId: Number(taskId), type: typeParam });
        }
    }, [taskId, getWorkOrderTaskDetail, id, type]);

    useEffect(() => {
        setData({
            title: pageTitle,
            subTitle: typeLabel.toUpperCase(),
            backLink: history.length > 1 ? history.goBack : route(WORKORDER_ROUTES.detail, { id }),
            showUser: false,
            machineId: '',
            projectId: '',
            showMachineMessages: false,
            isIssueDetailPage: false,
            notification: false,
            search: false,
        });
    }, [setData, id, pageTitle, typeLabel, history.length, history.goBack]);

    if (fetching) {
        return <Loader size="32" />;
    }

    return (
        <div className={classes.page}>
            <div className={classNames(classes.card, classes['header-card'])}>
                <div className={classes['header-main']}>
                    <div
                        className={classNames(
                            classes['type-icon-wrap'],
                            isIssue ? classes.issue : classes.service,
                        )}
                    >
                        <Icon name={isIssue ? 'warning' : 'build'} />
                    </div>
                    <div className={classes['header-text']}>
                        <p className={classes['type-label']}>{typeLabel}</p>
                        <h1 className={classes.title}>{taskData.title}</h1>
                    </div>
                </div>
                <span className={classNames(classes['status-badge'], taskData.type === IssueOrServiceType.ISSUE ? classes[`issue-${taskData.status}`] : classes[`service-${taskData.status}`])}>
                    {taskData.type === IssueOrServiceType.ISSUE ? tIssue(`statuses.${taskData.status}`) : tService(`statuses.${taskData.status}`)}
                </span>
            </div>

            <div className={classes.card}>
                <h2 className={classes['section-title']}>{t('detail.description')}</h2>
                <p className={classes['description-body']}>{descriptionBody}</p>
            </div>

            <Users users={users} />

            <Parts parts={parts} />

            <Materials materials={materials} />

            <Attachment attachments={taskData.attachments ?? []} />
        </div>
    );
};

export default Detail;
